// use client: scroll-spy activeItem state, scrollToElement, and router.push handlers
'use client'

import '@/styles/page.css'
import './guides.css'

import type { CmsItem } from '@/utils/api'
import { ASSETS_BASE_URL } from '@/constants'

import { useEffect, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { isEmpty } from 'lodash'
import { useParams, useRouter } from 'next/navigation'

import guideMain from '@/assets/images/guides/guide-person.webp'
import MYDp from '@/assets/images/guides/MYalrajhi.svg'
import Breadcrumbs from '@/components/breadcrumbs'
import Link from '@/components/Link'
import { Maintenance } from '@/components/maintenance'
import TagChip from '@/components/tagChip'
import { useIsMobile } from '@/hooks/useIsMobile'
import { convertMonthEnglishFormat, generateSlug, parseCmsHtml } from '@/utils'
import { cmsImageSrc } from '@/utils/cmsImageSrc'
import { ArticleCard } from './shared/ArticleCard'
import { PromotionCarousel } from './shared/PromotionCarousel'

function ArticleDetails({
  initialData,
  articleList = [],
  promotions = []
}: {
  initialData?: CmsItem<'/articles'> | null
  articleList?: CmsItem<'/articles'>[]
  promotions?: CmsItem<'/promotion-and-campaigns'>[]
}) {
  const [activeItem, setActiveItem] = useState('introduction')
  const { category, slug } = useParams()
  const router = useRouter()
  // Article, the same-category rail, and the promo rail are all resolved at build
  // time by app/personal/guides/[category]/[slug]/page.tsx. A Strapi publish
  // rebuilds and redeploys the site, so there is nothing to revalidate on mount.
  // See tests/no-client-cms-fetch.spec.ts.
  const guidesList = initialData
  const promotionsContent = promotions
  // `dynamicParams = false` means only known category/slug pairs are emitted, so
  // this is true on every real page; kept as the same guard the fetch used to set.
  const isData = initialData != null
  const isMobile = useIsMobile({ maxWidth: '360px' })
  const isTabScreen = useIsMobile({ maxWidth: '576px' })

  const scrollToElement = (_, hash) => {
    const element = document.querySelector(`#${hash}`)
    setActiveItem(hash)
    const offset = 120

    if (element) {
      const topPosition = element.getBoundingClientRect().top + window.pageYOffset - offset
      window.scrollTo({ top: topPosition, behavior: 'smooth' })
    }
  }

  const PROMO_RAIL_LIMIT = 6

  const slides = (promotionsContent ?? []).slice(0, PROMO_RAIL_LIMIT).map((content, index) => {
    const cleanTitle =
      typeof content.shortTitle === 'string' ? content.shortTitle.replaceAll(/<[^>]*>/g, '') : null

    return (
      <Link
        key={content.slug}
        href={`/personal/promotions/${content.slug}`}
        className="text-reset text-decoration-none d-block"
      >
        <div className="image-container">
          <img
            src={cmsImageSrc(content.imageLink)}
            alt={cleanTitle ? `${cleanTitle} (${content.slug})` : 'alrajhi bank promotion'}
            className="promotion-image object-fit-scale"
            loading={index === 0 ? 'eager' : 'lazy'}
            decoding="async"
            width={250}
            height={250}
          />
        </div>
        <div className="promotion-content mt-4 text-start">
          <h5>
            {typeof content.shortTitle === 'string'
              ? parseCmsHtml(content.shortTitle)
              : content.shortTitle}
          </h5>
        </div>
      </Link>
    )
  })

  useEffect(() => {
    const handleScroll = () => {
      const offset = 160
      let current = 'introduction'
      document.querySelectorAll('div[id]').forEach(section => {
        const sectionTop = (section as HTMLElement).offsetTop - offset
        if (window.scrollY >= sectionTop) {
          current = section.id
        }
      })
      setActiveItem(current)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return isData ? (
    <div id="article" className="container page">
      <Breadcrumbs />
      <Row className="justify-content-between">
        <Col lg={8}>
          <div id="introduction">
            <div style={{ marginBottom: '16px' }}>
              {guidesList?.article_tags?.map(tag => (
                <TagChip key={tag.title}>{tag.title}</TagChip>
              ))}
            </div>
            <h1 className="page-title">{guidesList?.title}</h1>
            <p className="page-subheading">{guidesList?.subtitle}</p>
            <div className="d-flex gap-1 gap-sm-2 align-items-center mb-3">
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  overflow: 'hidden'
                }}
              >
                <img
                  src={cmsImageSrc(guidesList?.profile_image?.imageLink) || MYDp.src}
                  alt="Display Picture"
                  style={{ display: 'block', width: '100%' }}
                />
              </div>
              <span
                style={{
                  color: 'var(--grey-800)',
                  fontWeight: '500'
                }}
              >
                {guidesList?.profile_name || 'MY alrajhi'}
              </span>
              <small>&#8226;</small>
              <span>{guidesList?.date ? convertMonthEnglishFormat(guidesList?.date) : null}</span>
              <small>&#8226;</small>
              <span>
                {guidesList
                  ? guidesList?.duration
                    ? guidesList?.duration
                    : `${(guidesList?.topics?.length ?? 0) + 1} min read`
                  : null}
              </span>
            </div>

            {/* Topic Index for Mobile Screen */}
            <Row className="d-block d-lg-none border-top py-3">
              <div className="article-topic-index-mobile">
                <ul>
                  <li>
                    <h6>On this page</h6>
                  </li>
                  <li
                    onClick={e => scrollToElement(e, 'introduction')}
                    className={activeItem === 'introduction' ? 'active' : ''}
                  >
                    Introduction
                  </li>
                  {guidesList?.topics?.map(item => (
                    <li
                      key={generateSlug(item.title)}
                      onClick={e => scrollToElement(e, generateSlug(item.title))}
                      className={activeItem === generateSlug(item.title) ? 'active' : ''}
                    >
                      {item.title}
                    </li>
                  ))}
                </ul>
              </div>
            </Row>

            <div className="mb-3 rounded-3 overflow-hidden">
              <img
                src={cmsImageSrc(guidesList?.image?.imageLink)}
                alt={guidesList?.image?.altText}
                style={{ display: 'block', width: '100%' }}
              />
            </div>
            <p
              style={{
                fontSize: '14px',
                fontStyle: 'italic',
                textAlign: 'center'
              }}
            >
              {guidesList?.image?.title}
            </p>
            <div>{guidesList?.description && parseCmsHtml(guidesList?.description)}</div>
          </div>
          <div className="article-topic-container">
            {guidesList?.topics?.map(item => (
              <div key={generateSlug(item.title)} id={generateSlug(item.title)} className="my-4">
                <h2 className="mb-4">{item.title}</h2>
                <div>{item.content && parseCmsHtml(item.content)}</div>
              </div>
            ))}
          </div>
          <div className="article-footer">
            <p className="fst-italic">
              <span className="fw-bold">Disclaimer:</span> The contents of this article are for
              general circulation and information purposes only and does not constitute investment
              research or advice or a recommendation to buy or sell investment/banking products.
              Please read the full disclaimer on alrajhi bank Malaysia's main page of the Guides
              Section.
            </p>
          </div>
          {/* Social Media Icons */}
          <div className="artcile-media-icon-container">
            <span>Share</span>

            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${ASSETS_BASE_URL}personal/guides/${category}/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="bi bi-facebook" style={{ color: '#333', fontSize: '20px' }}></i>
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`https://wa.me/?text=${ASSETS_BASE_URL}personal/guides/${category}/${slug}`}
            >
              <i className="bi bi-whatsapp" style={{ color: '#333', fontSize: '20px' }}></i>
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${ASSETS_BASE_URL}personal/guides/${category}/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="bi bi-linkedin" style={{ color: '#333', fontSize: '20px' }}></i>
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`https://twitter.com/intent/tweet?url=${ASSETS_BASE_URL}personal/guides/${category}/${slug}&text=Check%20this%20out!`}
            >
              <i className="bi bi-twitter-x" style={{ color: '#333', fontSize: '20px' }}></i>
            </a>
            <a
              href="#"
              onClick={e => {
                e.preventDefault()
                const url = `${ASSETS_BASE_URL}personal/guides/${category}/${slug}`
                navigator.clipboard
                  .writeText(url)
                  .then(() => {
                    alert('Link copied to clipboard!')
                  })
                  .catch(error => {
                    console.error('Failed to copy:', error)
                  })
              }}
              aria-label="Copy link to clipboard"
            >
              <i className="bi bi-link-45deg" style={{ color: '#333', fontSize: '20px' }}></i>
            </a>
          </div>

          {/* Promotion in Mobile Screen */}
          {!isEmpty(promotionsContent) && (
            <Row className="d-block d-lg-none border-top py-3">
              <div className="article-page-promotion">
                <h6>Promotions</h6>
                <PromotionCarousel>{slides}</PromotionCarousel>
              </div>
            </Row>
          )}

          {/* Similar Topics */}
          {!isEmpty(articleList?.filter(item => item?.slug !== slug)) && (
            <div data-aos="fade-up" data-aos-delay="150" className="my-5">
              <h2 className="page-title">Discover similar topics</h2>
              <Row className="my-4 d-flex">
                <Col
                  className="guide-latest-article-container"
                  data-aos="fade-left"
                  data-aos-delay="150"
                >
                  {articleList
                    ?.slice(0, 4)
                    .filter(item => item?.slug !== slug)
                    .map(item => (
                      <ArticleCard
                        key={item.slug}
                        item={item}
                        isMobile={isMobile}
                        isTabScreen={isTabScreen}
                        categoryBadge="compact"
                        imageFallbackSrc={guideMain.src}
                        imageAltFallback="Guide Main Image"
                        onClick={() =>
                          router.push(
                            `/personal/guides/${item?.guide_category?.slug ?? category}/${item.slug}/`
                          )
                        }
                      />
                    ))}
                </Col>
              </Row>
            </div>
          )}
        </Col>

        {/* Topic Index */}
        <Col lg={4} className="d-none d-lg-block">
          <div className="article-topic-index">
            <h6>On this page</h6>
            <ul>
              <li
                onClick={e => scrollToElement(e, 'introduction')}
                className={`topic-list ${activeItem === 'introduction' ? 'active' : ''}`}
              >
                Introduction
              </li>
              {guidesList?.topics?.map(item => (
                <li
                  key={generateSlug(item.title)}
                  onClick={e => scrollToElement(e, generateSlug(item.title))}
                  className={`topic-list ${
                    activeItem === generateSlug(item.title) ? 'active' : ''
                  }`}
                >
                  {item.title}
                </li>
              ))}
            </ul>
            {!isEmpty(promotionsContent) && (
              <div className="article-page-promotion">
                <h6>Promotions</h6>
                <PromotionCarousel>{slides}</PromotionCarousel>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </div>
  ) : (
    <Maintenance />
  )
}

export default ArticleDetails
